// What every crash screen shows under its headline: a reference to quote, what
// broke, where, and the last request that answered — plus "Copy details" so a
// tester or customer can paste it into a ticket. Shared by the app-level
// ErrorBoundary, PageBoundary and the router's RouteErrorFallback.
// The report itself (and what it never contains) is src/lib/clientErrorReport.ts.
import React, { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { formatErrorReport, type ClientErrorReport } from '@/lib/clientErrorReport';

export const ErrorReportDetails: React.FC<{ report: ClientErrorReport }> = ({ report }) => {
  const [copied, setCopied] = useState<'yes' | 'failed' | null>(null);
  const lr = report.lastRequest;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(formatErrorReport(report));
      setCopied('yes');
    } catch {
      setCopied('failed');
    }
  };

  return (
    <div className="w-full max-w-lg rounded-lg border border-border bg-muted p-3 text-left text-xs text-muted-foreground" data-testid="error-report">
      <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1">
        <dt className="font-medium text-foreground">Reference</dt>
        <dd className="font-mono break-all">{report.ref}</dd>
        <dt className="font-medium text-foreground">Time</dt>
        <dd className="font-mono">{report.at}</dd>
        <dt className="font-medium text-foreground">Page</dt>
        <dd className="font-mono break-all">{report.route}</dd>
        <dt className="font-medium text-foreground">Error</dt>
        <dd className="font-mono break-words">{report.message}{report.code ? ` [${report.code}]` : ''}</dd>
        <dt className="font-medium text-foreground">Last request</dt>
        <dd className="font-mono break-all">
          {lr
            ? `${lr.method} ${lr.path} → ${lr.status === 0 ? 'no response' : lr.status}${lr.code ? ` (${lr.code})` : ''}`
            : 'none recorded'}
        </dd>
      </dl>
      {report.stack && (
        <details className="mt-2">
          <summary className="cursor-pointer hover:text-foreground">Technical details</summary>
          <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-all">{report.stack}</pre>
        </details>
      )}
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => void copy()}
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1 text-xs font-medium text-foreground hover:bg-background"
        >
          {copied === 'yes' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied === 'yes' ? 'Copied' : 'Copy details'}
        </button>
        {copied === 'failed' && <span role="status">Could not copy — select the text above instead.</span>}
      </div>
    </div>
  );
};
